import React from 'react';
import { Info, ShieldCheck, ArrowRight } from 'lucide-react';
import { TRAIT_ORDER, TRAIT_META } from '../../../config/bigFiveMeta';

/**
 * Shared building blocks for the Talent Insights aggregate dashboards
 * (Big Five + DISC). Extracted from the original Big Five aggregate so
 * both inventories present k-anonymity progress, suppression callouts,
 * and summary stats identically.
 */

// IPIP-NEO-120 trait raw range: 24..120, midpoint 72. HR reads a 0-100
// "percent along the scale" rather than the raw psychometric units.
export const TRAIT_MIN = 24;
export const TRAIT_MAX = 120;

/** Raw trait mean (24..120) -> percent along the scale (0..100). */
export const meanToPercent = (mean) => ((mean - TRAIT_MIN) / (TRAIT_MAX - TRAIT_MIN)) * 100;

/**
 * Translate a scale percent (0..100) into a short qualitative bucket so HR
 * can read a value at a glance without doing math. Same thresholds used
 * for both the radar chart tooltip and the standout trait chips.
 *
 * Buckets: Low / Below average / Average / Above average / High
 */
export const bucketFromPercent = (pct) => {
  if (pct < 25) return { label: 'Low', tone: 'text-slate-600 bg-slate-100' };
  if (pct < 40) return { label: 'Below average', tone: 'text-blue-700 bg-blue-50' };
  if (pct < 60) return { label: 'Average', tone: 'text-gray-700 bg-gray-100' };
  if (pct < 75) return { label: 'Above average', tone: 'text-emerald-700 bg-emerald-50' };
  return { label: 'High', tone: 'text-violet-700 bg-violet-50' };
};

/**
 * Rank the five OCEAN traits by how far each sits from the scale's
 * midpoint (50%) — i.e. how "defining" that trait is for this group,
 * regardless of direction. Used to surface the 1-2 traits that most
 * distinguish a company/department benchmark, on the hero radar and on
 * each department mini-card.
 *
 * @param {Object} dimsByCode  `{ O: {mean, sd}, C: {...}, ... }`
 * @returns {Array<{code,pct,mean,sd,distance}>} sorted by distance desc
 */
export const rankTraitsByProminence = (dimsByCode) => TRAIT_ORDER
  .map((code) => {
    const dim = dimsByCode?.[code];
    if (!dim || dim.mean == null) return null;
    const pct = meanToPercent(dim.mean);
    return { code, pct, mean: dim.mean, sd: dim.sd, distance: Math.abs(pct - 50) };
  })
  .filter(Boolean)
  .sort((a, b) => b.distance - a.distance);

/**
 * Always-visible OCEAN colour key — five coloured dots + full trait
 * names, rendered as plain HTML (never SVG text). This is deliberate:
 * the radar chart's axis labels are colour-coded but kept to a single
 * letter for space, and SVG text can be inconsistently legible/zoomable
 * across mobile browsers (iOS Safari, Android Chrome) compared to
 * ordinary HTML. This key is the reliable, always-readable reference
 * for what each axis colour means, on every device.
 */
export const OceanColorKey = ({ className = '' }) => (
  <div className={`grid grid-cols-2 sm:grid-cols-5 gap-x-3 gap-y-2 ${className}`}>
    {TRAIT_ORDER.map((code) => {
      const meta = TRAIT_META[code];
      return (
        <div key={code} className="flex items-center gap-1.5 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: meta.hex }}
            aria-hidden="true"
          />
          <span className="text-[11px] sm:text-xs font-medium text-gray-700 truncate">
            {code === 'N' ? 'Emotional Sensitivity' : meta.name}
          </span>
        </div>
      );
    })}
  </div>
);

export const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 min-w-0">
    <div className="flex items-start gap-2 min-w-0">
      <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-red-600" />
      </div>
      <div className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide font-semibold min-w-0 leading-snug">
        {label}
      </div>
    </div>
    <div className="mt-2 text-lg sm:text-2xl font-bold text-gray-900 leading-tight break-words">
      {value}
    </div>
    {hint && (
      <div className="mt-1 text-[11px] text-gray-500 leading-snug line-clamp-3 sm:line-clamp-none">
        {hint}
      </div>
    )}
  </div>
);

/**
 * Empty state shown when no segment in the current view clears the k=5
 * anonymity gate. Concrete progress numbers so HR knows exactly where
 * the rollout stands.
 *
 * `assessmentName` parameterizes the copy ("…have completed the
 * <assessmentName>") so the same component serves both inventories.
 */
export const EmptyState = ({
  segmentType,
  eligibility,
  onDrillDownToDirectory,
  assessmentName = 'Big Five Profile',
}) => {
  const eligible = eligibility?.eligible_for_aggregation ?? 0;
  const k = eligibility?.k_threshold ?? 5;
  const submitted = eligibility?.unique_employees_submitted ?? 0;
  const perSegmentProgress = eligibility?.per_segment_progress || [];
  const segmentsReady = eligibility?.segments_meeting_threshold ?? 0;
  const isCompany = segmentType === 'company';

  // The third stat tile must reflect the question this view is asking:
  //   - Company view: is the company itself at k≥5?
  //   - Segment view: is at least one segment at k≥5?
  const readyForView = isCompany ? eligible >= k : segmentsReady > 0;
  const segmentLabel = segmentType === 'designation' ? 'role' : segmentType;

  // The closest segment is the best lever HR can pull — surface it
  // prominently so they know exactly who's nearest the threshold.
  const closest = perSegmentProgress[0];

  return (
    <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 sm:p-10">
      <div className="max-w-xl mx-auto text-center">
        <Info className="w-10 h-10 text-gray-400 mx-auto" />
        <h3 className="mt-3 text-base font-semibold text-gray-900">
          {isCompany
            ? 'No company benchmark to show yet'
            : `No ${segmentLabel} has enough profiles yet`}
        </h3>

        {/* Progress block — concrete numbers so HR knows exactly where they stand */}
        {eligibility && (
          <div className="mt-5 rounded-xl bg-gray-50 border border-gray-200 p-4 text-left">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900">{submitted}</div>
                <div className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                  Submitted
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{eligible}</div>
                <div className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                  Valid for benchmark
                </div>
              </div>
              <div
                className="col-span-2 sm:col-span-1"
                title={isCompany
                ? `The company benchmark publishes once ${k} or more profiles are valid.`
                : `A ${segmentLabel}-level benchmark publishes once any single ${segmentLabel} has ${k}+ profiles.`}
              >
                <div className={`text-2xl font-bold ${readyForView ? 'text-emerald-700' : 'text-gray-900'}`}>
                  {readyForView ? '✓' : '—'}
                </div>
                <div className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                  {isCompany
                    ? (readyForView ? 'Ready' : 'Not ready')
                    : `${segmentLabel}s ready`}
                </div>
                {!isCompany && (
                  <div className="mt-0.5 text-[10px] text-gray-500 tabular-nums">
                    {segmentsReady} of {perSegmentProgress.length}
                  </div>
                )}
              </div>
            </div>
            {submitted !== eligible && (
              <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600 leading-relaxed">
                <strong>{submitted - eligible}</strong> submitted profile{submitted - eligible === 1 ? '' : 's'} {submitted - eligible === 1 ? 'is' : 'are'} not counted toward the benchmark — typically because of incomplete data or a re-take that supersedes an earlier submission.
              </div>
            )}
          </div>
        )}

        {/* Per-segment progress — in the empty state we show the same
            "small segments → use Individual view" callout the populated
            state uses, so HR gets consistent affordances no matter
            which path landed them here. */}
        {!isCompany && perSegmentProgress.length > 0 && (
          <div className="mt-5">
            <SmallSegmentsCallout
              segmentType={segmentType}
              perSegmentProgress={perSegmentProgress}
              kThreshold={k}
              onDrillDown={onDrillDownToDirectory}
              closestHint={closest && !closest.meets_threshold ? closest : null}
              compact
            />
          </div>
        )}

        <p className="mt-4 text-sm text-gray-600">
          {isCompany ? (
            eligible >= k
              ? 'A benchmark should appear momentarily — refresh the page if it doesn\'t.'
              : <>The first benchmark is published once at least <strong>{k}</strong> colleagues have completed the {assessmentName}. This protects individual privacy.</>
          ) : (
            <>
              {eligible >= k
                ? <>The company-wide view already has enough profiles to publish — switch tabs to see it.</>
                : <>The first benchmark is published once at least <strong>{k}</strong> colleagues have completed the {assessmentName} in the same {segmentLabel}.</>}
              {' '}This protects individual privacy.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

/**
 * Callout listing segments that are below the k=5 anonymity threshold,
 * each with an action to drill down to the audited Individual Result
 * flow in the Employee Directory.
 *
 * Why this exists
 * ---------------
 * In small orgs many departments have <5 submitters (sometimes <5
 * total headcount). The aggregate view correctly hides those segments
 * to protect individual privacy, but doing so silently makes them
 * disappear from the page entirely — HR is left wondering where
 * Contract / Strategic / Legal went. This callout makes the
 * suppression transparent: it shows the segment name and submitter
 * count (operational rollout data, not psychometric scores), and
 * routes HR to the audited Individual Result flow which is the
 * correct tool for n=1/2 cohorts.
 *
 * Props
 * -----
 * - `segmentType`      'department' | 'designation' (used for labels).
 * - `perSegmentProgress`  full sorted list from `eligibility`; the
 *                      callout filters internally to below-threshold
 *                      rows so the caller doesn't need to pre-filter.
 * - `kThreshold`       publication threshold (typically 5).
 * - `onDrillDown(value)`  called with the segment value when the user
 *                      clicks "use Individual view". Parent should
 *                      switch to the Employees tab pre-filtered to
 *                      that value (the directory's existing free-text
 *                      search matches against department/designation).
 * - `closestHint`      optional explicit row to highlight at the top
 *                      ("Closest: Design & BIM 4 of 5"). Used in the
 *                      empty-state path where the closest-to-threshold
 *                      framing is the dominant message.
 * - `compact`          slightly tighter padding when used in the
 *                      empty-state card.
 */
export const SmallSegmentsCallout = ({
  segmentType,
  perSegmentProgress,
  kThreshold,
  onDrillDown,
  closestHint,
  compact,
}) => {
  const segmentLabel = segmentType === 'designation' ? 'role' : segmentType;
  const belowThreshold = (perSegmentProgress || []).filter((r) => !r.meets_threshold);
  if (belowThreshold.length === 0) return null;

  return (
    <div className={`rounded-2xl border border-amber-200 bg-amber-50/40 ${compact ? 'p-4' : 'p-5 sm:p-6'}`}>
      {/* Icon anchors only the title + description (DS indent-scope rule).
          The department list below is a sibling so it spans the full callout
          width — nesting it in the icon's flex-1 column left a ~48px dead
          gutter and looked left-shifted on mobile. */}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-4.5 h-4.5 text-amber-700" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900">
            {`Smaller ${segmentLabel}s not shown in the benchmark above`}
          </div>
          <div className="mt-0.5 text-xs text-gray-700 leading-relaxed">
            Groups with fewer than <strong>{kThreshold}</strong> completed profiles are hidden here to protect individual identities. For these {segmentLabel}s, open results one at a time from the <strong>Employee Directory</strong> — each open is justified and audited.
          </div>
          {closestHint && (
            <div className="mt-2 text-xs text-gray-700 leading-relaxed">
              Closest to publication: <strong>{closestHint.segment_value}</strong>{' '}
              ({closestHint.respondent_count} of {kThreshold} — <strong>{closestHint.shortfall}</strong> more
              {' '}{closestHint.shortfall === 1 ? 'profile' : 'profiles'} needed).
            </div>
          )}
        </div>
      </div>

      <ul className="mt-4 space-y-3">
        {belowThreshold.map((row) => {
          const pct = Math.min(100, (row.respondent_count / kThreshold) * 100);
          return (
            <li
              key={row.segment_value}
              className="p-4 flex flex-col gap-3 bg-white/70 border border-amber-200 rounded-xl sm:grid sm:grid-cols-12 sm:items-center"
            >
              <div className="w-full sm:col-span-5 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate" title={row.segment_value}>
                  {row.segment_value}
                </div>
                <div className="text-[11px] text-gray-600 tabular-nums">
                  {row.respondent_count} submitted · {row.shortfall} more for a group benchmark
                </div>
              </div>
              <div className="hidden sm:block sm:col-span-3">
                <div className="relative h-2 bg-white rounded-full overflow-hidden border border-amber-200">
                  <div
                    className="h-full bg-amber-400 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <div className="w-full sm:col-span-4 sm:text-right">
                {onDrillDown ? (
                  <button
                    type="button"
                    onClick={() => onDrillDown(row.segment_value)}
                    className="btn btn-admin-outline btn-sm w-full sm:w-auto"
                    title={`Open the Employee Directory filtered to ${row.segment_value}.`}
                  >
                    View {row.respondent_count} individually
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <span className="text-[11px] text-gray-500 italic">
                    Open in Employee Directory
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
